import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { Plus, Edit, Trash2, ArrowLeft, Check, TrendingUp, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import apiService from '../../utils/api';
import UserFriendlyDateInput from '../../components/UserFriendlyDateInput';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';

function WeeklyAnimalMilkPage() {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [khatas, setKhatas] = useState([]);
  const [tabelas, setTabelas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // View states
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'form'
  const [editingItem, setEditingItem] = useState(null);
  
  const [formData, setFormData] = useState({
    tag_number: '',
    record_date: new Date().toISOString().split('T')[0],
    milk_morning: '',
    milk_night: '',
    production_type: 'milk'
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  // List pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const [filters, setFilters] = useState({
    tag_number: '',
    tabela_id: '',
    khata_id: '',
    weeks_back: 52
  });

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const options = { 
      weekday: 'short',
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadWeeklyMilkRecords();
  }, [currentPage, filters, animals]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAnimals(),
        loadKhatas(),
        loadTabelas()
      ]);
      setError(null);
    } catch (error) {
      setError(error.message);
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeeklyMilkRecords = async () => {
    if (animals.length === 0) return;
    
    try {
      const promises = animals.map(async (animal) => {
        try {
          const params = {
            weeks_back: filters.weeks_back,
            page: 1,
            per_page: 200
          };
          
          const response = await apiService.getAnimalWeeklyMilk(animal.tag_number, params);
          return (response.data || []).map(record => ({
            ...record,
            animal_tag: animal.tag_number,
            animal_type: animal.type,
            khata_id: animal.khata_id,
            // FIX: explicitly preserve production_type from API, fallback to 'milk'
            production_type: record.production_type || 'milk'
          }));
        } catch (error) {
          console.warn(`Error loading milk for animal ${animal.tag_number}:`, error);
          return [];
        }
      });

      const allRecords = await Promise.all(promises);
      const flatRecords = allRecords.flat();
      
      // Apply filters
      let filteredRecords = flatRecords.filter(record => {
        if (filters.tag_number && !record.animal_tag.toString().toUpperCase().includes(filters.tag_number.toUpperCase())) return false;
        if (filters.khata_id && record.khata_id !== parseInt(filters.khata_id)) return false;
        if (filters.tabela_id) {
          const animal = animals.find(a => a.tag_number === record.animal_tag);
          if (animal) {
            const khata = khatas.find(k => k.khata_id === animal.khata_id);
            if (!khata || khata.tabela_id !== parseInt(filters.tabela_id)) return false;
          }
        }
        return true;
      });

      // Sort by record date (most recent first)
      filteredRecords.sort((a, b) => new Date(b.week_start) - new Date(a.week_start));
      
      setData(filteredRecords);
    } catch (error) {
      setError(error.message);
      console.error('Error loading milk records:', error);
    }
  };

  const loadAnimals = async () => {
    try {
      const response = await apiService.getAnimals();
      setAnimals(response.data || []);
    } catch (error) {
      console.error('Error loading animals:', error);
    }
  };

  const loadKhatas = async () => {
    try {
      const response = await apiService.getKhata();
      setKhatas(response.data || []);
    } catch (error) {
      console.error('Error loading khatas:', error);
    }
  };

  const loadTabelas = async () => {
    try {
      const response = await apiService.getTabela();
      setTabelas(response.data || []);
    } catch (error) {
      console.error('Error loading tabelas:', error);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      tag_number: '',
      record_date: new Date().toISOString().split('T')[0],
      milk_morning: '',
      milk_night: '',
      production_type: 'milk'
    });
    setCurrentView('form');
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      tag_number: item.animal_tag.toString(),
      record_date: item.week_start,
      milk_morning: item.total_morning.toString(),
      milk_night: item.total_night.toString(),
      // FIX: preserve production_type when editing, fallback to 'milk'
      production_type: item.production_type || 'milk'
    });
    setCurrentView('form');
  };

  const handleDelete = async (item) => {
    if (window.confirm(`Are you sure you want to delete the milk record for Animal #${item.animal_tag} (Date: ${formatDateDisplay(item.week_start)})?`)) {
      try {
        await apiService.deleteAnimalWeeklyMilk(item.animal_tag, item.record_id);
        await loadWeeklyMilkRecords();
      } catch (error) {
        toast.error('Error deleting record: ' + error.message);
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const tagNumber = formData.tag_number?.toString().trim();
      if (!tagNumber) throw new Error('Animal Tag Number is required.');
      if (!formData.record_date) throw new Error('Record Date is required.');
      
      if (formData.milk_morning === '' || formData.milk_morning === null || formData.milk_morning === undefined) {
        throw new Error('Morning Milk is required.');
      }
      if (formData.milk_night === '' || formData.milk_night === null || formData.milk_night === undefined) {
        throw new Error('Evening Milk is required.');
      }

      const normalizeDate = (val) => {
        if (!val) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
        return val;
      };

      const submitData = {
        record_date: normalizeDate(formData.record_date),
        record_type: 'daily',
        milk_morning: parseFloat(formData.milk_morning),
        milk_night: parseFloat(formData.milk_night),
        // FIX: always send production_type from formData, never hardcode
        production_type: formData.production_type || 'milk'
      };

      console.log('Submitting milk record:', submitData);

      if (isNaN(submitData.milk_morning) || isNaN(submitData.milk_night)) {
        throw new Error('Milk quantities must be valid numbers.');
      }

      setLoading(true);
      if (editingItem) {
        await apiService.updateAnimalWeeklyMilk(
          tagNumber, 
          editingItem.record_id, 
          submitData
        );
      } else {
        await apiService.createAnimalWeeklyMilk(tagNumber, submitData);
      }
      
      await loadWeeklyMilkRecords();
      setCurrentView('list');
      // FIX: include production_type in reset so state is clean
      setFormData({
        tag_number: '',
        record_date: new Date().toISOString().split('T')[0],
        milk_morning: '',
        milk_night: '',
        production_type: 'milk'
      });
      setEditingItem(null);
    } catch (error) {
      toast.error(error.message || 'Error saving record');
      console.error('Submit Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'tag_number') {
      const upperValue = String(value).toUpperCase();
      setFormData(prev => ({ ...prev, [name]: upperValue }));
      
      if (upperValue.trim()) {
        const matches = animals.filter(a => a.tag_number.toString().toUpperCase().includes(upperValue));
        setFilteredSuggestions(matches);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setCurrentView('list');
    // FIX: include production_type in reset
    setFormData({
      tag_number: '',
      record_date: new Date().toISOString().split('T')[0],
      milk_morning: '',
      milk_night: '',
      production_type: 'milk'
    });
    setEditingItem(null);
    setError(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      tag_number: '',
      tabela_id: '',
      khata_id: '',
      weeks_back: 52
    });
    setCurrentPage(1);
  };

  const getAnimalInfo = (tagNumber) => {
    const animal = animals.find(a => a.tag_number.toString().toUpperCase() === tagNumber.toString().toUpperCase());
    if (!animal) return { type: 'Unknown', khata_no: 'Unknown', tabela_name: 'Unknown' };
    
    const khata = khatas.find(k => k.khata_id === animal.khata_id);
    const tabela = tabelas.find(t => t.tabela_id === khata?.tabela_id);
    
    return {
      type: animal.type,
      khata_no: khata?.khata_no || 'Unknown',
      tabela_name: tabela?.name || 'Unknown'
    };
  };

  const getProductionLabel = (type) => {
    const map = { milk: 'Milk', fatwa: 'Fatwa', chick: 'Chick' };
    return map[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Milk');
  };

  const getProductionBadgeStyle = (type) => {
    if (type === 'fatwa') return { background: '#fef9c3', color: '#374151' };
    if (type === 'chick') return { background: '#dbeafe', color: '#374151' };
    return { background: '#dcfce7', color: '#374151' }; // milk (default)
  };

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">
          {content}
        </div>
      </div>
    </div>
  );

  // Pagination
  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = data.slice(startIndex, startIndex + itemsPerPage);

  if (loading && data.length === 0) {
    return renderLayout(
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading records...</p>
      </div>
    );
  }

  if (error && data.length === 0) {
    return renderLayout(
      <div className="error-container">
        <h3>Error loading data</h3>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>Retry</button>
      </div>
    );
  }

  // Form View
  if (currentView === 'form') {
    const totalDayMilk = (parseFloat(formData.milk_morning) || 0) + (parseFloat(formData.milk_night) || 0);

    return renderLayout(
      <>
        <div className="page-header">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Cancel
            </button>
            <h1 className="page-title">
              {editingItem ? 'Edit Milk Record' : 'Add Milk Record'}
            </h1>
          </div>
        </div>

        <div className="card" style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div className="form-row">
            <div className="form-group" style={{ position: 'relative' }}>
              <label htmlFor="tag_number" className="form-label">Animal Tag Number *</label>
              <input
                type="text"
                id="tag_number"
                name="tag_number"
                className="form-input"
                value={formData.tag_number}
                onChange={handleInputChange}
                onFocus={() => formData.tag_number.trim() && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                disabled={loading || !!editingItem}
                placeholder="Type tag number (e.g. A101)..."
                autoComplete="off"
              />
              
              {showSuggestions && filteredSuggestions.length > 0 && !editingItem && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 50,
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {filteredSuggestions.map(animal => {
                    const info = getAnimalInfo(animal.tag_number);
                    return (
                      <div
                        key={animal.tag_number}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border-color)'
                        }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({ ...prev, tag_number: animal.tag_number.toString().toUpperCase() }));
                          setShowSuggestions(false);
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <strong>#{animal.tag_number}</strong> - {animal.type} <br/>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          (Khata #{info.khata_no} - {info.tabela_name})
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="record_date" className="form-label">Record Date *</label>
              <UserFriendlyDateInput
                id="record_date"
                name="record_date"
                className="form-input"
                value={formData.record_date}
                onChange={handleInputChange}
                disabled={loading}
              />
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <strong>Select the specific day for this milk record</strong>
              </div>
            </div>
          </div>

          <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem', marginBottom: '1rem' }}>
            <h3 className="card-title" style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrendingUp size={18} /> Daily Production
            </h3>
          </div>

          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label htmlFor="production_type" className="form-label">Production Type *</label>
            <select
              id="production_type"
              name="production_type"
              className="form-select"
              value={formData.production_type}
              onChange={handleInputChange}
              disabled={loading}
            >
              <option value="milk">Milk</option>
              <option value="fatwa">Fatwa</option>
              <option value="chick">Chick</option>
            </select>
          </div>

          <div style={{ marginBottom: '1.5rem', padding: '1rem', borderRadius: 'var(--radius-md)', backgroundColor: '#eff6ff', color: '#1e40af', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Info size={16} /> Enter the milk collected on this specific day
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="milk_morning" className="form-label">Morning Milk (Liters) *</label>
              <input
                type="number"
                id="milk_morning"
                name="milk_morning"
                className="form-input"
                value={formData.milk_morning}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Morning milk"
                step="0.1"
                min="0"
              />
            </div>
            <div className="form-group">
              <label htmlFor="milk_night" className="form-label">Evening Milk (Liters) *</label>
              <input
                type="number"
                id="milk_night"
                name="milk_night"
                className="form-input"
                value={formData.milk_night}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Evening milk"
                step="0.1"
                min="0"
              />
            </div>
          </div>

          {(formData.milk_morning || formData.milk_night) && (
            <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '1.5rem', borderRadius: 'var(--radius-md)', marginTop: '1rem' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-primary)' }}>Production Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Day</div>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--primary-color)' }}>{totalDayMilk.toFixed(2)} L</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Morning</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{(parseFloat(formData.milk_morning) || 0).toFixed(2)} L</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Evening</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>{(parseFloat(formData.milk_night) || 0).toFixed(2)} L</div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '2rem' }}>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              <Check size={18} />
              {loading ? 'Saving...' : (editingItem ? 'Update Record' : 'Save Record')}
            </button>
          </div>
        </div>
      </>
    );
  }

  // List View
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title">Animal Milk Records</h1>
        <button className="btn btn-primary" onClick={handleAdd}>
          <Plus size={18} /> Add Record
        </button>
      </div>

      <div className="filter-bar">
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Animal Tag</label>
          <input
            type="text"
            name="tag_number"
            className="form-input"
            value={filters.tag_number}
            onChange={handleFilterChange}
            placeholder="Tag #"
          />
        </div>
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Tabela</label>
          <select
            name="tabela_id"
            className="form-select"
            value={filters.tabela_id}
            onChange={handleFilterChange}
          >
            <option value="">All Tabelas</option>
            {sortedByName(tabelas).map(tabela => (
              <option key={tabela.tabela_id} value={tabela.tabela_id}>
                {tabela.name}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Khata</label>
          <select
            name="khata_id"
            className="form-select"
            value={filters.khata_id}
            onChange={handleFilterChange}
          >
            <option value="">All Khatas</option>
            {sortedByName(khatas, k => k.khata_no).map(khata => {
              const tabela = tabelas.find(t => t.tabela_id === khata.tabela_id);
              return (
                <option key={khata.khata_id} value={khata.khata_id}>
                  Khata #{khata.khata_no} - {tabela?.name || 'Unknown'}
                </option>
              );
            })}
          </select>
        </div>
        <div className="filter-group">
          <label className="form-label" style={{marginBottom: 0, fontSize: '0.8rem'}}>Weeks Back</label>
          <select
            name="weeks_back"
            className="form-select"
            value={filters.weeks_back}
            onChange={handleFilterChange}
          >
            <option value={4}>Last 4 weeks</option>
            <option value={8}>Last 8 weeks</option>
            <option value={12}>Last 12 weeks</option>
            <option value={24}>Last 6 months</option>
            <option value={52}>Last 1 year</option>
            <option value={104}>Last 2 years</option>
            <option value={520}>All time</option>
          </select>
        </div>
        <button className="btn btn-secondary" onClick={clearFilters} style={{ height: '42px', alignSelf: 'flex-end' }}>
          Clear
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Animal</th>
              <th>Type</th>
              <th>Khata</th>
              <th>Tabela</th>
              <th>Record Date</th>
              <th>Production</th>
              <th>Morning</th>
              <th>Evening</th>
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map(item => {
              const animalInfo = getAnimalInfo(item.animal_tag);
              // FIX: use helper functions for consistent production type display
              const prodType = item.production_type || 'milk';
              return (
                <tr key={`${item.animal_tag}-${item.record_id}`}>
                  <td data-label="Animal">
                    <span style={{ fontWeight: 600, color: 'var(--primary-color)' }}>#{item.animal_tag}</span>
                  </td>
                  <td data-label="Type">
                    <span className={`type-badge ${(item.animal_type || '').toLowerCase()}`}>
                      {item.animal_type}
                    </span>
                  </td>
                  <td data-label="Khata">#{animalInfo.khata_no}</td>
                  <td data-label="Tabela">{animalInfo.tabela_name}</td>
                  <td data-label="Record Date">
                    <strong>{formatDateDisplay(item.week_start)}</strong>
                  </td>
                  <td data-label="Production">
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      ...getProductionBadgeStyle(prodType)
                    }}>
                      {getProductionLabel(prodType)}
                    </span>
                  </td>
                  <td data-label="Morning">{item.total_morning.toFixed(2)}L</td>
                  <td data-label="Evening">{item.total_night.toFixed(2)}L</td>
                  <td data-label="Total">
                    <strong style={{ color: 'var(--success-color)' }}>
                      {item.total_week.toFixed(2)}L
                    </strong>
                  </td>
                  <td data-label="Actions">
                    <div className="actions-cell">
                      <button 
                        className="btn-icon"
                        onClick={() => handleEdit(item)}
                        disabled={loading}
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="btn-icon"
                        style={{ color: 'var(--error-color)' }}
                        onClick={() => handleDelete(item)}
                        disabled={loading}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {currentItems.length === 0 && !loading && (
          <div className="no-data">
            <p>No milk records found</p>
            {(filters.tag_number || filters.tabela_id || filters.khata_id) && (
              <button className="btn btn-secondary btn-sm" onClick={clearFilters} style={{ marginTop: '1rem' }}>
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft size={16} />
          </button>
          
          <span style={{ margin: '0 1rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      
      {data.length > 0 && (() => {
        const t = data.reduce((acc, r) => {
          acc.morn += parseFloat(r.total_morning) || 0;
          acc.eve  += parseFloat(r.total_night)   || 0;
          return acc;
        }, { morn: 0, eve: 0 });
        return (
          <div style={{ marginTop: '1rem', padding: '1rem 1.2rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
            <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Records (filter)</div><div style={{ fontWeight: 700 }}>{data.length}</div></div>
            <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Morning (L)</div><div style={{ fontWeight: 700 }}>{t.morn.toFixed(2)}</div></div>
            <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total Evening (L)</div><div style={{ fontWeight: 700 }}>{t.eve.toFixed(2)}</div></div>
            <div><div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Grand Total (L)</div><div style={{ fontWeight: 700, color: 'var(--primary-color)' }}>{(t.morn + t.eve).toFixed(2)}</div></div>
          </div>
        );
      })()}

      <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Showing {currentItems.length} of {data.length} records
      </div>
    </>
  );
}

export default WeeklyAnimalMilkPage;